import { isArray, isEqual, isNumber, isObject, isString } from 'lodash';
import { OTHERS_ANSWER } from 'schema/constants';
import type { Question } from 'schema/poll';
import type { AnswerState } from 'schema/state';
import { InterpreterIntl } from '../machine/interpreter';
import { findLastInStack, notApplicable, questionHasAnswers, questionHasInvalidAnswers } from '../utils';

// TODO: В будущем нужно будет переместить intl внутрь компонентов и визуализацию тоже,
// а в функции валидации только возвращать код\enum ошибки, возможно с флагом / отдельным enum для "невидимых" ошибок

/** Текст некоторых ошибок мы не хотим показывать. Тогда используется эта непустая строка с невидимыми символами */
export const INVISIBLE_ERROR = ' ';

interface QuestionForValidation {
  id: string;
  data: {
    type: Question['data']['type'];
    requiresAnswer?: boolean;
    hasAnswerCountLimit?: boolean;
    answerCountMinimum?: number;
    answerCountMaximum?: number;
    maximumRank?: number;
    allowRetryUntilCorrectAnswer?: boolean;
    answers?: { id: string; isCorrect?: boolean }[];
    hasComment?: boolean | undefined;
    commentLimit?: number | undefined;
    hasOthers?: boolean | undefined;
    othersLimit?: number | undefined;
    textLimit?: number | undefined;
    textareaType?: string | undefined;
    othersLabel?: string;
    othersRequired?: boolean;
    cardSortingAllowUncomplete?: boolean;
  } & object;
}

const validateCardSorting = ({
  question,
  answersStack,
  relevantQuestions,
  intl,
}: {
  question: QuestionForValidation;
  answersStack: AnswerState[];
  relevantQuestions: QuestionForValidation[];
  intl: InterpreterIntl;
}) => {
  const answerState = findLastInStack(answersStack, question.id);
  if (!question.data.requiresAnswer) {
    return undefined;
  }

  const answerIds = new Set(answerState.answersIds);
  const answeredIds = relevantQuestions.reduce<Set<string>>((result, q) => {
    const answeredIds = findLastInStack(answersStack, q.id).answeredIds ?? [];
    answeredIds.forEach(id => result.add(id));
    return result;
  }, new Set());

  if (question.data.cardSortingAllowUncomplete && !answeredIds.size) {
    return intl.CardSortingAllowUncompleteError;
  }

  if (!question.data.cardSortingAllowUncomplete && !isEqual(answerIds, answeredIds)) {
    return intl.CardSortingRequiredError;
  }

  return undefined;
};

export function validateAnswers(
  question: QuestionForValidation,
  relevantQuestions: QuestionForValidation[],
  answersStack: AnswerState[],
  intl: InterpreterIntl,
): string | undefined {
  const onChangeError = validateAnswersOnChange(question, relevantQuestions, answersStack, intl);
  const onValidateError = validateAnswersOnSubmit(question, relevantQuestions, answersStack, intl);

  if (onChangeError === INVISIBLE_ERROR && !!onValidateError) return onValidateError; // предпочитаем видимые ошибки
  return onChangeError || onValidateError;
}

export function validateAnswersOnSubmit(
  question: QuestionForValidation,
  relevantQuestions: QuestionForValidation[],
  answersStack: AnswerState[],
  intl: InterpreterIntl,
): string | undefined {
  const answerState = findLastInStack(answersStack, question.id);
  const { answeredIds = [], isWithoutAnswers, answeredOthers } = answerState;

  // Пропустить если ответ не был выбран
  if (!question.data.requiresAnswer && !answeredIds.length && !answeredOthers) return;
  // Пропустить если ответ "не применимо"
  if (notApplicable(answerState)) return;

  if (question.data.type === 'POLL_CARD_SORTING_CATEGORY') {
    return validateCardSorting({ question, relevantQuestions, answersStack, intl });
  }

  if (!isWithoutAnswers && !questionHasAnswers(question.data, answerState) && question.data.requiresAnswer)
    return intl.RequiredAnswersNotGiven;

  if (isMultiple(question.data.type)) {
    const { hasAnswerCountLimit, answerCountMinimum = 0 } = question.data;
    if (hasAnswerCountLimit && answeredIds.length < answerCountMinimum) {
      return intl.PleaseSelectAtLeastAnswers({ count: answerCountMinimum });
    }
  }

  if (question.data.hasOthers && answeredIds.includes(OTHERS_ANSWER)) {
    const answers: unknown[] =
      'answers' in question.data && isArray(question.data.answers) ? question.data.answers : [];
    const otherAnswer = answers.find(
      (answer: unknown): answer is object => isObject(answer) && 'otherAnswer' in answer && !!answer.otherAnswer,
    );

    const otherAnswerText = otherAnswer && 'text' in otherAnswer && isString(otherAnswer.text) ? otherAnswer.text : '';
    const fallbackLabel = question.data.type === 'POLL_RANK' ? intl.PollRankAddOther : intl.Other;

    const { othersLabel = fallbackLabel, othersRequired } = question.data;
    const hasContent = answeredOthers && answeredOthers.trim().length > 0;
    if (othersRequired && !hasContent) {
      return intl.FillingFieldMandatory({ label: otherAnswerText || othersLabel });
    }
  }

  const textLimitError = validateTextLimit({ intl, question, answeredOthers });

  if (textLimitError) {
    return textLimitError;
  }

  return;
}

export function validateAnswersOnChange(
  question: QuestionForValidation,
  relevantQuestions: QuestionForValidation[],
  answersStack: AnswerState[],
  intl: InterpreterIntl,
): string | undefined {
  const answerState = findLastInStack(answersStack, question.id);
  const { answeredIds = [], isWithoutAnswers, answeredOthers, errorMessage: previousError } = answerState;

  if (question.data.type === 'POLL_CARD_SORTING_CATEGORY') {
    return validateCardSorting({ question, relevantQuestions, answersStack, intl }) ? INVISIBLE_ERROR : undefined;
  }

  if (!isWithoutAnswers && !questionHasAnswers(question.data, answerState) && question.data.requiresAnswer)
    return INVISIBLE_ERROR;

  const selectedAnswer = question.data.answers?.filter(answer => answeredIds.includes(answer.id) && answer.isCorrect);

  const disabledByNotCorrectAnswer = question.data.allowRetryUntilCorrectAnswer && !selectedAnswer?.length;
  if (disabledByNotCorrectAnswer) return INVISIBLE_ERROR; // Выключаем кнопку до тех пор, пока не будет выбран правильный ответ

  const textLimitError = validateTextLimit({ intl, question, answeredOthers });

  if (textLimitError && textLimitError === previousError) {
    return textLimitError;
  }

  if (questionHasInvalidAnswers(question.data, answerState)) return INVISIBLE_ERROR;

  // Пропустить если ответ не был выбран
  if (!question.data.requiresAnswer && !answeredIds.length && !answeredOthers) return;
  // Пропустить если ответ "не применимо"
  if (notApplicable(answerState)) return;

  if (isMultiple(question.data.type)) {
    const { hasAnswerCountLimit, answerCountMaximum = question.data.answers?.length ?? 0 } = question.data;
    if (hasAnswerCountLimit && answeredIds.length > answerCountMaximum) {
      return intl.MaxAnswersCountReached;
    }
  }

  if (question.data.type === 'POLL_RANK') {
    const { maximumRank } = question.data;
    if (maximumRank != null && answeredIds.length > maximumRank) {
      return intl.MaxAnswersCountReached;
    }
  }

  return;
}

export function validateUnanswerable(
  question: {
    data: {
      type: Question['data']['type'];
      percentToUnlockMedia?: number;
    };
  },
  answerState: AnswerState,
  intl: InterpreterIntl,
) {
  if (
    question.data.type === 'MEDIA_FILE_ANY' ||
    question.data.type === 'MEDIA_FILE_AUDIO' ||
    question.data.type === 'MEDIA_FILE_IMAGE' ||
    question.data.type === 'MEDIA_FILE_TEXT' ||
    question.data.type === 'MEDIA_FILE_VIDEO'
  ) {
    const isPercentToUnlockNotReached =
      !!question.data.percentToUnlockMedia && (answerState.mediaProgress ?? 0) < question.data.percentToUnlockMedia;

    if (isPercentToUnlockNotReached) {
      return question.data.type === 'MEDIA_FILE_AUDIO'
        ? intl.AudioPercentToUnlockError
        : intl.VideoPercentToUnlockError;
    }
  }

  return;
}
const isMultiple = (q: Question['data']['type']) =>
  q === 'POLL_MULTIPLE' ||
  q === 'POLL_MULTIPLE_FROM_PICTURE' ||
  q === 'POLL_MULTIPLE_SELECT' ||
  q === 'MATRIX_POLL_MULTIPLE';

const validateTextLimit = ({
  intl,
  question,
  answeredOthers,
}: {
  question: {
    data: {
      type: Question['data']['type'];
      answers?: { id: string; isCorrect?: boolean }[];
      commentLimit?: number | undefined;
      othersLimit?: number | undefined;
      textLimit?: number | undefined;
      hasComment?: boolean | undefined;
    } & object;
  };
  answeredOthers: string | undefined;
  intl: InterpreterIntl;
}) => {
  const answeredOthersLength = answeredOthers?.length ?? 0;
  const answers = 'answers' in question.data && isArray(question.data.answers) ? question.data.answers ?? [] : [];

  const defaultLimit = 255;

  const hasOthers = answers.some(answer => 'otherAnswer' in answer && answer.otherAnswer);
  const othersLimit = isNumber(question.data.othersLimit) ? question.data.othersLimit : defaultLimit;
  const textLimit = isNumber(question.data.textLimit) ? question.data.textLimit : defaultLimit;
  const commentLimit = isNumber(question.data.commentLimit) ? question.data.commentLimit : defaultLimit;

  const isOtherLimitReached = hasOthers && answeredOthersLength > othersLimit;
  const isCommentLimitReached = question.data.hasComment && answeredOthersLength > commentLimit;
  let isTextareaLimitReached = false;

  if (question.data.type === 'POLL_TEXTAREA') {
    if ('textareaType' in question.data && question.data.textareaType === 'SINGLE_LINES_TEXT') {
      const textList = (answeredOthers ?? '').split(';');
      isTextareaLimitReached = textList.some(text => text.length > textLimit);
    } else {
      isTextareaLimitReached = answeredOthersLength > textLimit;
    }
  }

  if (isTextareaLimitReached || isOtherLimitReached || isCommentLimitReached) {
    return intl.TextLimitError;
  }

  return '';
};
